import { useCallback, useEffect, useState } from 'react';

import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { NotesManager } from 'model/notesManager.model';
import { GetNotesResponseType } from 'model/tasks.model';
import { TaskService } from 'service/task.service';

export const useTaskNotes = (
  taskId: string,
  startOperation: StartOperationType,
  completeOperation: CompleteOperationType,
): NotesManager<GetNotesResponseType> => {
  const [notes, setNotes] = useState<GetNotesResponseType[]>([]);
  const [loading, setLoading] = useState(false);

  const addNote = async (content: string) => {
    try {
      startOperation('upload', 'Adding note');
      await TaskService.postNote({
        taskId,
        noteContent: content,
      });
      completeOperation('upload', 'Note added successfully');
      await fetchNotes();
    } catch (err: any) {
      completeOperation('upload', `Failed to add note`, true);
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    try {
      setLoading(true);
      startOperation('update', 'Updating note');
      await TaskService.editNote({
        noteId,
        noteContent: content,
      });
      completeOperation('update', 'Note updated successfully');

      await fetchNotes();
    } catch (err: any) {
      completeOperation('upload', `Failed to updated the note`, true);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (taskId: string) => {
    try {
      setLoading(true);
      startOperation('delete', 'Deleting note');
      await TaskService.deleteNote(taskId);
      await fetchNotes();
      completeOperation('delete', 'Note deleted successfully');
    } catch (err: any) {
      completeOperation('delete', `Failed to delete the note`, true);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await TaskService.getNotes(taskId);
      setNotes(res.notes || []);
    } catch (err: any) {
      completeOperation('fetch', `Failed to fetch the notes`, true);
    } finally {
      setLoading(false);
    }
  }, [taskId, setNotes]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return { fetchNotes, notes, loading, deleteNote, updateNote, addNote };
};
